import React, { useCallback, useMemo, useState } from 'react';
import { useAsyncFn, useToggle } from 'react-use';
import { useHistory } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import { __t } from '@mssgme/shared';
import { getContourColor } from '@mssgme/helpers';
import { CheckableItem, ListInput } from '@mssgme/ui';
import { ThemedModal } from '../../../UI';
import { useCoreContext, useFrontAPI, useModalThemes, usePaymentConfigs, useSavedOrders } from '../../../hooks';
import { printPrice } from '../../printPrice';
import { Row } from '../../Row';
import { OrderItemsList } from '../../OrderItem';
import { PAYMENT_PROVIDER_RENDERERS, PAYMENT_PROVIDERS } from '../Providers';
import { StepsFooter } from './StepsFooter';

import styles from './PaymentStep.scss';

const ONLINE_KEY = 'online';

function PaymentStep() {
    const history = useHistory();
    const api = useFrontAPI();
    const payments = usePaymentConfigs();
    const { currentOrder, removeCurrentOrderId } = useSavedOrders();
    const { router } = useCoreContext();
    const { contentStyle } = useModalThemes();
    const [isPayNowModal, togglePayNowModal] = useToggle(false);

    const { immediate, online } = useMemo(() => {
        const mapper = ([provider]) => [provider, PAYMENT_PROVIDER_RENDERERS[provider]];

        return {
            immediate: payments.immediate.map(mapper),
            online: payments.online.map(mapper),
        };
    }, [payments]);
    const hasOnlineMethods = !!online.length;
    const hasImmediateMethods = !!immediate.length;
    const isJustOneOnlinePaymentMethods = hasOnlineMethods && !hasImmediateMethods;

    const [{ loading }, handlePay] = useAsyncFn(
        async (paymentMethod) => {
            await api.orders.finalize({ orderId: currentOrder._id, payload: { paymentMethod } });

            togglePayNowModal();
            removeCurrentOrderId();
            history.push(router.orders.toStatus(currentOrder._id, 'paid'));
        },
        [router, currentOrder]
    );

    const [selected, setSelected] = useState(hasOnlineMethods ? ONLINE_KEY : immediate[0]?.[0]);

    const optionStyles = useMemo(() => {
        const { backgroundColor, color, border } = getContourColor(
            contentStyle.backgroundColor,
            contentStyle.buttonBackground,
            contentStyle.buttonTextColor
        );

        return {
            option: {
                backgroundColor,
                color,
                border: `1px solid ${border}`,
            },
            active: {
                backgroundColor: contentStyle.buttonBackground,
                color: contentStyle.buttonTextColor,
                border: `1px solid ${contentStyle.buttonBackground}`,
            },
        };
    }, [contentStyle]);
    const options = useMemo(
        () =>
            currentOrder &&
            [
                hasOnlineMethods && {
                    value: ONLINE_KEY,
                    label: __t('pay_now'),
                    icon: (
                        <div>
                            {online.map(([provider]) => {
                                const found = PAYMENT_PROVIDERS.find((i) => i.provider === provider);

                                return found && <found.icon key={provider} />;
                            })}
                        </div>
                    ),
                },
                ...immediate.map(([key]) => ({
                    value: key,
                    label: __t(`payments_${key}`),
                })),
            ].filter(Boolean),
        [currentOrder, online, immediate]
    );

    const renderOption = useCallback(
        ({ option, active }) => {
            const style = active ? optionStyles.active : optionStyles.option;

            return (
                <CheckableItem
                    className={styles.choiceWrapper}
                    contentClass={styles.choiceContent}
                    style={style}
                    active={active}
                    color={style.color}
                >
                    {option.label}
                    {option.icon}
                </CheckableItem>
            );
        },
        [optionStyles]
    );

    const handleNext = useCallback(() => {
        if (selected === ONLINE_KEY) {
            togglePayNowModal(true);
        } else {
            void handlePay(selected);
        }
    }, [selected, handlePay]);

    if (!currentOrder) {
        return null;
    }

    const onlineElements = (
        <div className={styles.paymentButtons}>
            {online.map(([key, Renderer]) => (
                <Renderer key={key} order={currentOrder} disabled={loading} onPay={handlePay} />
            ))}
        </div>
    );

    return (
        <div>
            <OrderItemsList order={currentOrder} />
            <Row label={__t('total_to_pay')} value={printPrice(currentOrder.total)} style={{ marginBottom: 16 }} />

            {isJustOneOnlinePaymentMethods ? (
                onlineElements
            ) : (
                <div style={{ marginBottom: -8 }}>
                    <ListInput
                        containerClassName={styles.choices}
                        choiceClassName={styles.choice}
                        value={selected}
                        options={options}
                        renderOption={renderOption}
                        onChange={setSelected}
                    />

                    {hasOnlineMethods && (
                        <AnimatePresence>
                            {isPayNowModal && (
                                <ThemedModal
                                    overlay
                                    compact
                                    padding
                                    title={__t('choose_payment_method')}
                                    onClose={togglePayNowModal}
                                >
                                    {onlineElements}
                                </ThemedModal>
                            )}
                        </AnimatePresence>
                    )}
                </div>
            )}

            <StepsFooter prev next={!isJustOneOnlinePaymentMethods && handleNext} disabled={loading} />
        </div>
    );
}

export default PaymentStep;
